import React, { memo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { TechnicianOrderRow } from './TechnicianOrderRow';
import { emServicoQueueRank } from '../constants/boardGroups';

/**
 * Agrupa ordens por cidade dentro das colunas de técnicos.
 * Inclui sinalização de retorno (os_request_status = codstatus "OS de retorno") igual à coluna Em aberto.
 * memo (shallow): re-render só quando props mudam de referência — updates imutáveis do Board não defasam.
 * Rows usam compare visual (TechnicianOrderRow) para pular re-render irrelevante.
 */
function TechnicianCityGroup({
  cidade,
  ordens,
  technician,
  groupName,
  isDroppable = true,
  onBoardOrderDragStart,
  onBoardOrderDragEnd,
  showDefeitoColumn = false,
  codstatusRetorno = '',
  codstatusPedidoVinculadoDisponivel = '',
  extraRetornoStatuses = [],
  selectedOrders = [],
  setContextMenu,
  handleOrderClick,
  userShowOSConfig = 0,
  defeitosData = {},
  loadingDefeitos = false,
}) {
  const [cellTooltip, setCellTooltip] = useState(null);
  useEffect(() => {
    if (!cellTooltip?.triggerRect) return;
    const { left, top, width, height } = cellTooltip.triggerRect;
    const onMove = (e) => {
      if (e.clientX < left || e.clientX > left + width || e.clientY < top || e.clientY > top + height) {
        setCellTooltip(null);
      }
    };
    document.addEventListener('mousemove', onMove, { passive: true });
    return () => document.removeEventListener('mousemove', onMove);
  }, [cellTooltip]);

  const getSLAPriority = (sla) => {
    switch (sla) {
      case 'Vencido':
      case 'vencido':
        return 1;
      case 'Vencendo':
      case 'vencendo':
        return 2;
      case 'Em dia':
      case 'ok':
        return 3;
      default:
        return 4;
    }
  };

  const sortedOrdens = [...ordens].sort((a, b) => {
    const ra = emServicoQueueRank(a);
    const rb = emServicoQueueRank(b);
    if (ra !== rb) return ra - rb;
    const priorityA = getSLAPriority(a.sla);
    const priorityB = getSLAPriority(b.sla);
    if (priorityA !== priorityB) return priorityA - priorityB;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });

  return (
    <>
      <div className="technician-city-group">
        <div className="technician-city-header">
          <span className="technician-city-title">{cidade}</span>
          <span className="technician-city-count">{ordens.length}</span>
        </div>
        <div className="technician-city-orders">
          {sortedOrdens.map((ordem) => (
            <TechnicianOrderRow
              key={ordem.id}
              ordem={ordem}
              technician={technician}
              groupName={groupName}
              selectedOrders={selectedOrders}
              isSelected={selectedOrders.includes(ordem.id)}
              setContextMenu={setContextMenu}
              handleOrderClick={handleOrderClick}
              onBoardOrderDragStart={onBoardOrderDragStart}
              onBoardOrderDragEnd={onBoardOrderDragEnd}
              showDefeitoColumn={showDefeitoColumn}
              codstatusRetorno={codstatusRetorno}
              codstatusPedidoVinculadoDisponivel={codstatusPedidoVinculadoDisponivel}
              extraRetornoStatuses={extraRetornoStatuses}
              userShowOSConfig={userShowOSConfig}
              defeitosData={defeitosData}
              loadingDefeitos={loadingDefeitos}
              onCellTooltip={setCellTooltip}
            />
          ))}
        </div>
      </div>
      {cellTooltip &&
        ReactDOM.createPortal(
          <div
            className="board-cell-tooltip-portal"
            style={{
              position: 'fixed',
              left: cellTooltip.x,
              top: cellTooltip.y,
              transform: 'translateX(-50%)',
              zIndex: 99999,
              pointerEvents: 'none',
            }}
          >
            <span className="board-cell-tooltip-arrow" />
            <span className="board-cell-tooltip-text">{cellTooltip.text}</span>
          </div>,
          document.body
        )}
    </>
  );
}

const TechnicianCityGroupMemo = memo(TechnicianCityGroup);
TechnicianCityGroupMemo.displayName = 'TechnicianCityGroup';

export { TechnicianCityGroupMemo as TechnicianCityGroup };
export default TechnicianCityGroupMemo;
